import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { matches } from './SearchBar';

// Liste déroulante de tous les sujets, avec un champ pour filtrer la liste (sans accents ni majuscules).
// subjects : [{ id, name, count? }] — current : id du sujet affiché (ou null) —
// allHref : lien « tous les sujets » — hrefFor(id) : lien d'un sujet — unit : libellé du compteur.
export default function SubjectPicker({ subjects, current = null, allHref, hrefFor, unit = '', label = 'Choisir un sujet' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const input = useRef(null);
  const listRef = useRef(null);

  const sorted = useMemo(() => [...subjects].sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })), [subjects]);
  const shown = useMemo(() => sorted.filter(s => matches(q, s.name)), [sorted, q]);
  // « Tous les sujets » est la première entrée quand le filtre est vide
  const options = useMemo(() => (q.trim() ? [] : [{ id: 'all', name: 'Tous les sujets' }]).concat(shown), [shown, q]);
  const cur = subjects.find(s => s.id === current);

  const close = () => { setOpen(false); setQ(''); };
  const choose = (o) => { close(); navigate(o.id === 'all' ? allHref : hrefFor(o.id)); };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (root.current && !root.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [open]);

  useEffect(() => { if (open && input.current) input.current.focus(); }, [open]);
  useEffect(() => { setActive(0); }, [q, open]);
  useEffect(() => {
    const el = listRef.current && listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(options.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && options[active]) { e.preventDefault(); choose(options[active]); }
  };

  if (!subjects.length) return null;
  return (
    <div className="subj-picker" ref={root}>
      <button type="button" className={`subj-picker-btn ${open ? 'open' : ''}`} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}>
        <span className="subj-picker-label">{cur ? cur.name : label}</span>
        <svg viewBox="0 0 24 24" className="subj-picker-caret" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div className="subj-picker-panel" onKeyDown={onKey}>
          <input ref={input} type="search" className="subj-picker-filter" value={q} onChange={e => setQ(e.target.value)}
            placeholder={`Filtrer les ${subjects.length} sujets…`} aria-label="Filtrer la liste des sujets" autoComplete="off" spellCheck="false" />
          <ul className="subj-picker-list" role="listbox" ref={listRef}>
            {options.map((o, i) => (
              <li key={o.id} role="option" aria-selected={o.id === current}
                className={`${i === active ? 'active' : ''} ${o.id === current ? 'current' : ''} ${o.id === 'all' ? 'all' : ''}`}
                onMouseEnter={() => setActive(i)} onClick={() => choose(o)}>
                <span>{o.name}</span>
                {o.count != null && <small>{o.count}{unit ? ` ${unit}` : ''}</small>}
              </li>
            ))}
            {options.length === 0 && <li className="subj-picker-empty" role="presentation">aucun sujet pour « {q} »</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
